import { inject, onBeforeUnmount, provide, watch, type InjectionKey, type Ref } from 'vue'

/**
 * How the banner reaches a field.
 *
 * The chips in MissingFieldsBanner name a field, and switching to its step is
 * only half of an answer — the user still has to find it among five others.
 * Focusing it is the other half, and that needs the element, which lives three
 * components down and carries a `useId` the wizard never sees.
 *
 * So fields announce themselves instead: each registers the element it draws
 * itself in, and the wizard asks the registry for one by name. Provide/inject
 * rather than a prop chain because the steps in between (WizardFields, the
 * bespoke steps) have nothing to do with focus and should not have to forward
 * it — and because a step that does not register a field simply cannot be
 * focused, which is the honest outcome rather than a silent no-op.
 */
export type WizardFocusRegistry = {
  /** Announces the wrapper a field is drawn in. Re-registering a name replaces it. */
  register: (name: string, el: Ref<HTMLElement | null | undefined>) => void
  unregister: (name: string) => void
  /** Focuses the field's first control. False when there is nothing to focus. */
  focus: (name: string) => boolean
}

const WIZARD_FOCUS = Symbol('wizard-focus') as InjectionKey<WizardFocusRegistry>

/*
 * The first thing inside a field a user can act on, in document order. Covers
 * every control the wizard draws: an input, a textarea, a segmented date field
 * (spinbuttons), and a select or combobox, whose Reka trigger is a button.
 *
 * `:not([tabindex="-1"])` because Reka gives a combobox's own trigger and a
 * date field's hidden input a negative tabindex — neither is what the user
 * reaches by tabbing, so neither is what focus should land on.
 */
const FOCUSABLE =
  'input:not([type="hidden"]):not([tabindex="-1"]), textarea, [role="spinbutton"], select, button:not([tabindex="-1"])'

export const createWizardFocus = (): WizardFocusRegistry => {
  const fields = new Map<string, Ref<HTMLElement | null | undefined>>()

  return {
    register: (name, el) => {
      fields.set(name, el)
    },
    unregister: name => {
      fields.delete(name)
    },
    focus: name => {
      const wrapper = fields.get(name)?.value
      if (!wrapper) return false

      const control = wrapper.querySelector<HTMLElement>(FOCUSABLE)
      if (!control) return false

      control.focus()
      /*
       * `nearest`, so a field already on screen is not yanked to the top of a
       * dialog the user is reading. Focus alone does not scroll a container
       * that was scrolled programmatically.
       */
      control.scrollIntoView({ block: 'nearest' })
      return true
    },
  }
}

/** Called by the wizard, once. */
export const provideWizardFocus = () => {
  const registry = createWizardFocus()
  provide(WIZARD_FOCUS, registry)
  return registry
}

/**
 * Called by whatever draws a field, with the element it draws it in.
 *
 * A no-op outside a wizard: TagPicker and the summary cards use these same
 * components in places where nothing is looking for a field by name.
 */
export const useWizardFieldFocus = (
  name: () => string,
  el: Ref<HTMLElement | null | undefined>,
) => {
  const registry = inject(WIZARD_FOCUS, undefined)
  if (!registry) return

  watch(
    name,
    (next, previous) => {
      // A field definition can be swapped for another under the same component
      // — the type field's options are resolved per step — so the old name goes
      // with it rather than being left pointing at this element.
      if (previous && previous !== next) registry.unregister(previous)
      registry.register(next, el)
    },
    { immediate: true },
  )

  onBeforeUnmount(() => registry.unregister(name()))
}
